import React, { useRef, useContext, useState, useMemo } from "react"
import { Image } from "@chakra-ui/react"
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil"
import { badgesSelector, badgeIdsSelector } from "../../../recoil/atoms/badges"
import { bannerIdsSelector } from "../../../recoil/atoms/banners"
import { chatIdsSelector } from "../../../recoil/atoms/chats"
import { overlaySelector } from "../../../recoil/atoms/overlay"
import MouseHoverButtons from "../../common/mouseHoverButtons"
import { SDKContext } from "../../../context/SDKContext"
import { BadgeModel } from "../../../recoil/atoms/badges/types"
import { brandingSelector } from "../../../recoil/atoms/room"
import { usePictureInPicture } from "../../../hooks/usePictureInPicture"
import {
  Container,
  Wrapper,
  ImageContainer,
  InfoContainer,
  Title,
  Description,
  Stack,
  PrimaryNumber,
  SecondaryNumber
} from "./badge.styled"
import BuyButton from "./components/buy-button"
import { BadgeProps } from "./badges.interface"
import { cleanProductDescription, stripHtmlTags, toPixelString } from "../../../util"
import { OverlayModel } from "../../../recoil/atoms/overlay/types"

/**
 * Badge shown to moderator in livesell
 */
const Badge = React.memo(({ badge }: BadgeProps) => {
  const badgeRef = useRef<HTMLDivElement>(null)

  const [hovered, setHovered] = useState<boolean>(false)
  const setBadge = useSetRecoilState(badgesSelector(badge.id))
  const setBanners = useSetRecoilState(bannerIdsSelector)
  const setBadges = useSetRecoilState(badgeIdsSelector)
  const setChats = useSetRecoilState(chatIdsSelector)
  const roomStyles = useRecoilValue(brandingSelector)

  const {
    id,
    title,
    description,
    currentPrice,
    oldPrice,
    image,
    published,
    buttonText,
    checkoutUrl,
    shortDescription
  } = badge as BadgeModel

  const [currentOverlay, setCurrentOverlay] = useRecoilState(overlaySelector)
  const meetingClient = useContext(SDKContext)

  const toggleBadge = React.useCallback(() => {
    if (published) {
      setBadge({ published: false })
      currentOverlay && meetingClient?.unPublishOverlay(id, "badge")
    } else {
      if (currentOverlay) {
        if (currentOverlay.type === "banner")
          setBanners({
            name: "UPDATE",
            id: currentOverlay.id,
            attributes: { published: false }
          })
        if (currentOverlay.type === "badge")
          setBadges({
            name: "UPDATE",
            id: currentOverlay.id,
            attributes: { published: false }
          })
        if (currentOverlay.type === "chat")
          setChats({
            type: "UPDATE",
            payload: { id: currentOverlay.id, attributes: { published: false } }
          })
      }
      //Publish new Badge
      setBadge({ published: true })
      /* const data = !currentOverlay ? 1 : currentOverlay.id */

      let data = {}

      if (!currentOverlay) {
        data = {
          type: "badge",
          height: badgeRef?.current?.offsetHeight,
          published: true
          /* deleted: true,  */
        }
      } else {
        data = {
          type: "badge",
          height: badgeRef?.current?.offsetHeight,
          published: true,
          unpublishedId: currentOverlay.id
        }
      }

      // Setting current overlay
      setCurrentOverlay({
        ...badge,
        ...data
      } as OverlayModel)

      meetingClient?.publishOverlay(data, {
        id: badge.id,
        productSource: badge?.productSource
      })
    }
  }, [
    badge,
    currentOverlay,
    id,
    meetingClient,
    published,
    setBadge,
    setBadges,
    setBanners,
    setChats,
    setCurrentOverlay
  ])

  const { openExternalUrl } = usePictureInPicture()

  const handleOpenUrl = React.useCallback(() => {
    openExternalUrl(checkoutUrl)
  }, [openExternalUrl, checkoutUrl])

  const isSmallSizePrice = useMemo(() => {
    return (
      (currentPrice?.length > 6 || oldPrice?.length > 6) &&
      currentPrice?.length !== 0 &&
      oldPrice?.length !== 0
    )
  }, [currentPrice?.length, oldPrice?.length])

  const isImage = React.useMemo(() => {
    return image !== "" && image?.length > 0
  }, [image])

  const handleMouseEnter = React.useCallback(() => {
    setHovered(true)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    setHovered(false)
  }, [])

  // show hover button when mouse is hover or badge is published
  const showHoverButton = hovered || published

  return (
    <Container
      key={id}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={badgeRef}
      roomStyles={roomStyles}
    >
      <Wrapper>
        {isImage && (
          <ImageContainer>
            <Image
              maxW="max-content"
              src={image}
              alt={title}
              maxHeight="65px"
              maxWidth="65px"
              objectFit="contain"
              opacity={showHoverButton ? "0.2" : 1}
            />
          </ImageContainer>
        )}
        <MouseHoverButtons
          isPublished={published}
          isHovered={showHoverButton}
          toggleOverlay={toggleBadge}
        />
        <InfoContainer hovered={showHoverButton} isImage={isImage}>
          <Title roomStyles={roomStyles}>{title}</Title>
          <Description roomStyles={roomStyles}>
            {cleanProductDescription(
              stripHtmlTags(
                shortDescription && shortDescription !== ""
                  ? shortDescription
                  : description
              )
            )}
          </Description>
          <Wrapper>
            <Stack style={{ flexWrap: "wrap" }}>
              <PrimaryNumber
                roomStyles={roomStyles}
                isSmallSizePrice={isSmallSizePrice}
              >
                {currentPrice}
              </PrimaryNumber>
              <SecondaryNumber
                isSmallSizePrice={isSmallSizePrice}
                roomStyles={roomStyles}
              >
                {oldPrice}
              </SecondaryNumber>
            </Stack>
            <BuyButton
              handleClick={handleOpenUrl}
              textButton={buttonText}
              customStyles={{
                borderRadius: toPixelString(
                  roomStyles?.badgeButtonBorderRadius
                ),
                _hoverStyles: {
                  borderColor:
                    roomStyles?.badgeButtonBorderMouseoverColor || null
                }
              }}
            />
          </Wrapper>
        </InfoContainer>
      </Wrapper>
    </Container>
  )
})

export default Badge
